import {
  decimal,
  formatNative,
  type OpenComposition,
  type OpenPreviewResponse,
  type Pool,
  type QuoteRequest,
  type QuoteRequestBody,
  type FundingSnapshot,
} from "@auto-pools/core";
import { useEffect, useRef, useState } from "react";
import { api, ApiError, reportPreviewDiagnostic } from "../api/client";
import { dexName } from "../lib/dex";
import { formatToken, formatUsd } from "../lib/format";
import type { PriceFrame } from "../lib/priceFrame";

class PreviewTimingError extends Error {
  constructor() {
    super("Preview timing is invalid or expired. Retry preview.");
  }
}

function elapsedClock() {
  const wall = Date.now(),
    monotonic = performance.now();
  let elapsed = 0;
  // Wall time covers system sleep; retain observed age across backwards clock jumps.
  return () => (elapsed = Math.max(elapsed, Date.now() - wall, performance.now() - monotonic));
}

interface PreviewTiming {
  start: number;
  elapsed: () => number;
  ageAtReceipt: number;
}

// eslint-disable-next-line react-refresh/only-export-components -- Bounded preview module owns validation and display.
export function validatePreview(
  value: OpenPreviewResponse,
  request: QuoteRequest,
  wallet: FundingSnapshot["wallet"],
  pool: Pool,
  serverNow: number,
): void {
  const fail = (): never => {
    throw new Error(
      "Preview is unavailable or does not match this funding request. Retry preview.",
    );
  };
  if (
    value.requestKey !== JSON.stringify(request) ||
    value.wallet.address !== wallet.address ||
    value.wallet.generation !== wallet.generation
  )
    fail();
  const start = Date.parse(value.calculationStartedAt),
    end = Date.parse(value.calculationCompletedAt);
  if (
    !Number.isFinite(serverNow) ||
    !Number.isFinite(start) ||
    !Number.isFinite(end) ||
    new Date(start).toISOString() !== value.calculationStartedAt ||
    new Date(end).toISOString() !== value.calculationCompletedAt ||
    end < start ||
    end > serverNow ||
    serverNow - start >= 30_000
  )
    throw new PreviewTimingError();
  const c = value.composition;
  const numeric = (v: string | null, signed = false) => {
    if (
      v !== null &&
      (typeof v !== "string" ||
        v.length > 160 ||
        !/^-?\d+(\.\d+)?$/.test(v) ||
        (!signed && decimal(v).units < 0n))
    )
      fail();
  };
  const basis: unknown = c.basis;
  if (
    (basis !== "observed-pool-price" && basis !== "simulated-post-swap-price") ||
    c.fundingSwapPending !== (c.swap !== null)
  )
    fail();
  numeric(c.poolPrice);
  if (
    decimal(c.poolPrice).units <= 0n ||
    typeof c.liquidityDelta !== "string" ||
    !/^\d{1,39}$/.test(c.liquidityDelta) ||
    BigInt(c.liquidityDelta) <= 0n ||
    BigInt(c.liquidityDelta) >= 1n << 128n
  )
    fail();
  const amount = (a: OpenComposition["depositA"], mint: string) => {
    const token = [pool.tokenA, pool.tokenB].find((t) => t.mint === mint);
    if (
      !token ||
      typeof a.native !== "string" ||
      a.mint !== mint ||
      !/^(0|[1-9]\d{0,19})$/.test(a.native) ||
      BigInt(a.native) >= 1n << 64n ||
      a.decimal !== formatNative(BigInt(a.native), token.decimals)
    )
      fail();
    numeric(a.usd);
    if (a.native === "0" && a.usd !== "0") fail();
    return BigInt(a.native);
  };
  const minimumFields = [
    c.minimumDepositA,
    c.minimumDepositB,
    c.minimumTotalDepositUsd,
    c.minimumLiquidity,
    c.minimumSqrtPrice,
    c.maximumSqrtPrice,
  ];
  const hasMinimums = minimumFields.some((field) => field !== undefined);
  if (basis === "simulated-post-swap-price" || hasMinimums) {
    const required = <T,>(field: T | undefined): T => {
      if (field === undefined) return fail();
      return field;
    };
    const minimumA = required(c.minimumDepositA),
      minimumB = required(c.minimumDepositB),
      minimumTotalDepositUsd = required(c.minimumTotalDepositUsd),
      minimumLiquidity = required(c.minimumLiquidity),
      minimumSqrtPrice = required(c.minimumSqrtPrice),
      maximumSqrtPrice = required(c.maximumSqrtPrice);
    if (typeof minimumLiquidity !== "string") fail();
    if (typeof minimumSqrtPrice !== "string") fail();
    if (typeof maximumSqrtPrice !== "string") fail();
    const positiveU128 = (field: string) =>
      /^[1-9]\d{0,38}$/.test(field) && BigInt(field) < 1n << 128n;
    if (
      !positiveU128(minimumLiquidity) ||
      !positiveU128(minimumSqrtPrice) ||
      !positiveU128(maximumSqrtPrice) ||
      BigInt(minimumSqrtPrice) > BigInt(maximumSqrtPrice)
    )
      fail();
    amount(minimumA, pool.tokenA.mint);
    amount(minimumB, pool.tokenB.mint);
    numeric(minimumTotalDepositUsd);
    const minimumTotal =
      minimumA.usd === null || minimumB.usd === null
        ? null
        : decimal(minimumA.usd).add(decimal(minimumB.usd)).toString();
    if (minimumTotalDepositUsd !== minimumTotal) fail();
  }
  const a = amount(c.depositA, pool.tokenA.mint) + amount(c.residualA, pool.tokenA.mint);
  const b = amount(c.depositB, pool.tokenB.mint) + amount(c.residualB, pool.tokenB.mint);
  const fundingA = request.fundingMint === pool.tokenA.mint;
  const selected = fundingA ? a : b,
    other = fundingA ? b : a;
  if (c.swap) {
    if (!request.autoSwap) fail();
    const s = c.swap,
      outputMint = fundingA ? pool.tokenB.mint : pool.tokenA.mint;
    const input = amount(s.input, request.fundingMint),
      expected = amount(s.expectedOutput, outputMint),
      minimum = amount(s.minimumOutput, outputMint);
    if (
      input <= 0n ||
      minimum <= 0n ||
      minimum > expected ||
      amount(s.tradeFee, request.fundingMint) > input ||
      selected + input !== BigInt(request.amountNative) ||
      other !== expected
    )
      fail();
    if (
      !/^\d{1,4}$/.test(s.priceImpactBps) ||
      BigInt(s.priceImpactBps) > BigInt(request.caps.maxPriceImpactBps)
    )
      fail();
    numeric(s.valueDifferenceUsd, true);
    numeric(s.valueDifferencePercent, true);
    const diff =
      s.input.usd === null || s.expectedOutput.usd === null
        ? null
        : decimal(s.expectedOutput.usd).sub(decimal(s.input.usd));
    const pct =
      diff === null || s.input.usd === null || decimal(s.input.usd).units === 0n
        ? null
        : diff.mul(decimal("100")).div(decimal(s.input.usd), 12).toString();
    if (s.valueDifferenceUsd !== (diff?.toString() ?? null) || s.valueDifferencePercent !== pct)
      fail();
  } else if (selected !== BigInt(request.amountNative) || other !== 0n) fail();
  numeric(c.totalDepositUsd);
  const total =
    c.depositA.usd === null || c.depositB.usd === null
      ? null
      : decimal(c.depositA.usd).add(decimal(c.depositB.usd)).toString();
  if (c.totalDepositUsd !== total) fail();
}

// eslint-disable-next-line react-refresh/only-export-components -- Keep the scoped preview lifecycle with its display.
export function useOpenPreview(
  context: string,
  displayContext: string,
  enabled: boolean,
  active: boolean,
  now: number,
  pool: Pool,
  prepare: (
    forced: boolean,
  ) => Promise<{ request: QuoteRequestBody; wallet: FundingSnapshot["wallet"] } | null>,
) {
  const [state, setState] = useState<{
    key: string;
    displayKey: string;
    result?: OpenPreviewResponse;
    timing?: PreviewTiming;
    error?: string;
  }>({ key: "", displayKey: "" });
  const [pending, setPending] = useState(false);
  const [retry, setRetry] = useState(0);
  const refreshContext = useRef<string | null>(null);
  const [visible, setVisible] = useState(document.visibilityState !== "hidden");
  const [, setFocusAt] = useState(now);
  const current = useRef({ context, enabled, active, prepare });
  current.current = { context, enabled, active, prepare };
  const controller = useRef<AbortController | null>(null);
  const previousTiming = useRef<PreviewTiming | null>(null);
  const isFresh = () =>
    enabled &&
    state.key === context &&
    !pending &&
    !state.error &&
    !!state.result &&
    state.timing !== undefined &&
    state.timing.ageAtReceipt + state.timing.elapsed() < 30_000;
  const fresh = isFresh();
  useEffect(() => {
    if (state.timing === undefined) return;
    const timer = window.setTimeout(
      () => {
        setFocusAt((n) => n + 1);
      },
      Math.max(0, 30_000 - state.timing.ageAtReceipt - state.timing.elapsed()),
    );
    return () => {
      window.clearTimeout(timer);
    };
  }, [state.timing]);
  useEffect(() => {
    const focus = () => {
      setVisible(document.visibilityState !== "hidden");
      setFocusAt((n) => n + 1);
    };
    window.addEventListener("focus", focus);
    document.addEventListener("visibilitychange", focus);
    return () => {
      controller.current?.abort();
      window.removeEventListener("focus", focus);
      document.removeEventListener("visibilitychange", focus);
    };
  }, []);
  useEffect(() => {
    if (
      !active ||
      !visible ||
      !enabled ||
      fresh ||
      pending ||
      (state.key === context && state.error)
    )
      return;
    const timer = window.setTimeout(() => {
      const abort = new AbortController();
      controller.current = abort;
      const matches = () =>
        !abort.signal.aborted &&
        current.current.context === context &&
        current.current.enabled &&
        current.current.active &&
        document.visibilityState !== "hidden";
      setPending(true);
      void (async () => {
        const transport: { received: boolean; requestId?: string } = { received: false };
        try {
          const forced = refreshContext.current === context;
          refreshContext.current = null;
          const prepared = await current.current.prepare(forced);
          if (!prepared || !matches()) return;
          const { request, wallet } = prepared;
          const requestElapsed = elapsedClock();
          let serverNow = Number.NaN;
          const result = await api<OpenPreviewResponse>("/positions/preview", {
            method: "POST",
            body: request,
            sensitive: true,
            signal: abort.signal,
            onResponse: (response) => {
              transport.received = true;
              transport.requestId = response.headers.get("x-request-id") ?? undefined;
              const time = response.headers.get("x-auto-pools-time");
              if (time !== null && /^\d{13}$/.test(time)) serverNow = Number(time);
            },
          });
          const elapsed = elapsedClock();
          if (!matches()) return;
          try {
            validatePreview(result, request, wallet, pool, serverNow);
          } catch (reason) {
            if (reason instanceof PreviewTimingError) throw reason;
            throw new Error("Preview unavailable or mismatched. Retry preview.");
          }
          const start = Date.parse(result.calculationStartedAt);
          // Charge the full round trip, including body receipt, as a conservative age bound.
          let ageAtReceipt = serverNow - start + requestElapsed();
          const previous = previousTiming.current;
          if (previous && start <= previous.start)
            ageAtReceipt = Math.max(ageAtReceipt, previous.ageAtReceipt + previous.elapsed());
          previousTiming.current = {
            start: Math.max(start, previous?.start ?? start),
            elapsed,
            ageAtReceipt,
          };
          if (!Number.isFinite(ageAtReceipt) || ageAtReceipt >= 30_000)
            throw new PreviewTimingError();
          setState({
            key: context,
            displayKey: displayContext,
            result,
            timing: previousTiming.current,
          });
        } catch (reason) {
          if (matches() && transport.received)
            reportPreviewDiagnostic(
              reason instanceof PreviewTimingError
                ? "PREVIEW_TIMING_INVALID"
                : "PREVIEW_RESPONSE_INVALID",
              transport.requestId,
            );
          if (matches())
            setState((previous) => ({
              key: context,
              displayKey: displayContext,
              result:
                previous.displayKey === displayContext &&
                !(reason instanceof ApiError && reason.status === 401)
                  ? previous.result
                  : undefined,
              error:
                transport.received && !(reason instanceof PreviewTimingError)
                  ? "Preview unavailable or mismatched. Retry preview."
                  : reason instanceof Error
                    ? reason.message
                    : "Preview unavailable. Retry preview.",
            }));
        } finally {
          if (!abort.signal.aborted) setPending(false);
        }
      })();
    }, 400);
    return () => {
      window.clearTimeout(timer);
    };
  }, [
    context,
    displayContext,
    active,
    enabled,
    visible,
    fresh,
    pending,
    state.key,
    state.error,
    retry,
    pool,
  ]);
  return {
    isFresh,
    result: fresh && enabled ? state.result : undefined,
    // Retained data is presentation only; callers must gate progression on result.
    displayResult: state.displayKey === displayContext && enabled ? state.result : undefined,
    pending,
    error: state.key === context ? state.error : undefined,
    retry: () => {
      refreshContext.current = context;
      setState({ key: "", displayKey: "" });
      setRetry((n) => n + 1);
    },
  };
}

export function PositionPreview({
  composition: c,
  pool,
  caps,
  frame,
}: {
  composition: OpenComposition;
  pool: Pool;
  caps: QuoteRequest["caps"];
  frame: PriceFrame;
}) {
  const tokenSymbol = (value: OpenComposition["depositA"]) =>
    value.mint === pool.tokenA.mint ? pool.tokenA.symbol : pool.tokenB.symbol;
  const show = (label: string, value: OpenComposition["depositA"], emphasized = false) => {
    const symbol = value.mint === pool.tokenA.mint ? pool.tokenA.symbol : pool.tokenB.symbol;
    const short = formatToken(value.decimal),
      display = /e/i.test(short) ? value.decimal : short;
    return (
      <div className={`preview-amount ${emphasized ? "deposit" : ""}`}>
        <span>{label}</span>
        <strong title={value.decimal}>
          {display !== value.decimal ? "≈ " : ""}
          {display} {symbol}
        </strong>
        <span>{value.usd === null ? "Unavailable" : `≈ ${formatUsd(value.usd)}`}</span>
      </div>
    );
  };
  const signed = (value: string | null, usd = false) => {
    if (value === null) return "Unavailable";
    const formatted = usd ? formatUsd(value) : formatToken(value);
    return `${decimal(value).units > 0n ? "+" : ""}${formatted}`;
  };
  const exact = (
    label: string,
    value: OpenComposition["depositA"],
  ): readonly [string, OpenComposition["depositA"]] => [label, value];
  const exactAmounts = [
    exact("Deposit " + pool.tokenA.symbol, c.depositA),
    exact("Deposit " + pool.tokenB.symbol, c.depositB),
    exact("Unused " + pool.tokenA.symbol, c.residualA),
    exact("Unused " + pool.tokenB.symbol, c.residualB),
    ...(c.swap
      ? [
          exact("Swap input", c.swap.input),
          exact("Expected swap output", c.swap.expectedOutput),
          exact("Minimum swap output", c.swap.minimumOutput),
          exact("Pool trading fee", c.swap.tradeFee),
        ]
      : []),
  ];
  return (
    <section className="position-preview stack" aria-label="Position preview">
      <h3>Position composition</h3>
      <div className="preview-grid">
        {show(`Deposit ${pool.tokenA.symbol}`, c.depositA, true)}
        {show(`Deposit ${pool.tokenB.symbol}`, c.depositB, true)}
      </div>
      <p className="preview-total">
        Estimated position value:{" "}
        {c.totalDepositUsd === null ? "Unavailable" : `≈ ${formatUsd(c.totalDepositUsd)}`}
      </p>
      {c.swap && (
        <p className="composition-swap">
          {formatToken(c.swap.input.decimal)} {tokenSymbol(c.swap.input)} →{" "}
          {formatToken(c.swap.expectedOutput.decimal)} {tokenSymbol(c.swap.expectedOutput)}
          <span>
            via {dexName(pool.dex)} pool · value change {signed(c.swap.valueDifferenceUsd, true)}
          </span>
        </p>
      )}
      <details className="composition-details">
        <summary>Composition details</summary>
        <div>
          <p className="hint">
            Current observed price: {formatToken(frame.price(c.poolPrice))} {frame.unit}.
          </p>
        </div>
        <p className="preview-notice">
          This is an estimate at the observed price.
          {c.fundingSwapPending
            ? " Your own swap can move the price, so deposits and unused amounts may change."
            : " Final deposits can change before opening."}
        </p>
        {c.swap ? (
          <>
            <h4>Funding swap</h4>
            <div className="preview-grid">
              {show(`Swap ${tokenSymbol(c.swap.input)}`, c.swap.input)}
              {show(
                `Expected to receive ${tokenSymbol(c.swap.expectedOutput)}`,
                c.swap.expectedOutput,
              )}
              {show(
                `Minimum to receive ${tokenSymbol(c.swap.minimumOutput)}`,
                c.swap.minimumOutput,
              )}
              <div className="preview-amount">
                <span>Estimated value change</span>
                <strong>{signed(c.swap.valueDifferenceUsd, true)}</strong>
                <span>
                  {c.swap.valueDifferencePercent === null
                    ? "Unavailable"
                    : `${signed(c.swap.valueDifferencePercent)}% of swap value`}
                </span>
              </div>
            </div>
          </>
        ) : (
          <p className="hint">No funding swap is needed.</p>
        )}
        <div className="preview-grid compact">
          {show(`Estimated unused ${pool.tokenA.symbol}`, c.residualA)}
          {show(`Estimated unused ${pool.tokenB.symbol}`, c.residualB)}
        </div>
        <details className="preview-details">
          <summary>Fees, limits and exact amounts</summary>
          {c.swap && show(`Pool trading fee in ${tokenSymbol(c.swap.tradeFee)}`, c.swap.tradeFee)}
          <p className="hint">
            The trading fee is already included in the value change. Slippage limit:{" "}
            {formatNative(BigInt(caps.maxSlippageBps), 2)}%. Network fee limit:{" "}
            {formatNative(BigInt(caps.maxNetworkFeeLamports), 9)} SOL, including priority fees up to{" "}
            {formatNative(BigInt(caps.maxPriorityFeeLamports), 9)} SOL. Account rent limit:{" "}
            {formatNative(BigInt(caps.maxRentLamports), 9)} SOL; refundable rent is separate from
            swap value change.
          </p>
          <div className="exact-values-grid">
            {exactAmounts.map(([label, value]) => (
              <label key={label}>
                {label}, exact
                <input aria-label={`Exact ${label}`} readOnly value={value.decimal} />
              </label>
            ))}
          </div>
          <p className="hint">
            Unused amounts only describe this position's selected funding, not the rest of your
            wallet. The exact quote and final market checks can still stop the opening.
          </p>
        </details>
      </details>
    </section>
  );
}
